import { useState, type FormEvent } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { siteConfig } from '@/lib/utils'

type Status = 'idle' | 'sending' | 'ok' | 'error'

const WEB3FORMS_KEY = import.meta.env.VITE_WEB3FORMS_KEY || ''

export function Contato() {
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState<string>('')

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!WEB3FORMS_KEY) {
      setStatus('error')
      setErrorMsg(
        'Form não está configurado. Cadastre a access key em https://web3forms.com e adicione como VITE_WEB3FORMS_KEY.'
      )
      return
    }

    setStatus('sending')
    setErrorMsg('')
    const form = e.currentTarget
    const data = new FormData(form)
    data.set('access_key', WEB3FORMS_KEY)
    data.set('from_name', `Site i3 — ${data.get('name')}`)
    data.set('subject', `[Site i3] Contato — ${data.get('service') || 'sem serviço'}`)

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: data,
      })
      const json = await res.json()
      if (res.ok && json.success) {
        setStatus('ok')
        form.reset()
      } else {
        setStatus('error')
        setErrorMsg(json.message || 'Falha ao enviar. Tenta de novo daqui a pouco.')
      }
    } catch (err) {
      setStatus('error')
      setErrorMsg('Sem conexão. Tenta de novo ou manda direto pro email abaixo.')
    }
  }

  return (
    <>
      <Helmet>
        <title>Contato — i3 Solutions</title>
        <meta
          name="description"
          content="Fale com a i3 Solutions. Orçamentos, projetos, parcerias. Resposta em 1 dia útil."
        />
        <link rel="canonical" href="https://i3solutions.tech/contato" />
      </Helmet>

      <section className="border-b border-border/30">
        <div className="container py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold tracking-wider text-primary uppercase mb-3">
              Contato
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-balance">
              Vamos conversar sobre o seu projeto
            </h1>
            <p className="mt-6 text-lg text-muted-foreground text-pretty">
              Conta o problema, o contexto e o resultado esperado. Em 1 dia útil a gente volta com
              direção concreta — não com proposta inflada.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container grid lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 space-y-6"
          >
            <a
              href={`mailto:${siteConfig.email}`}
              className="flex items-start gap-4 p-5 rounded-xl border border-border/50 bg-card hover:border-primary/40 transition-colors"
            >
              <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Mail className="size-5 text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Email</div>
                <div className="font-semibold">{siteConfig.email}</div>
              </div>
            </a>

            <a
              href={siteConfig.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-5 rounded-xl border border-border/50 bg-card hover:border-primary/40 transition-colors"
            >
              <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Phone className="size-5 text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp</div>
                <div className="font-semibold">{siteConfig.phone}</div>
              </div>
            </a>

            <div className="flex items-start gap-4 p-5 rounded-xl border border-border/50 bg-card">
              <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="size-5 text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Endereço</div>
                <div className="font-semibold">
                  {siteConfig.address.street}, {siteConfig.address.neighborhood}
                </div>
                <div className="text-sm text-muted-foreground">
                  {siteConfig.address.city}/{siteConfig.address.state}, {siteConfig.address.country}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="lg:col-span-3 p-6 md:p-8 rounded-2xl border border-border/50 bg-card space-y-5"
          >
            {/* honeypot anti-spam */}
            <input
              type="checkbox"
              name="botcheck"
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
            />

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input id="name" name="name" type="text" required placeholder="Seu nome" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail *</Label>
                <Input id="email" name="email" type="email" required placeholder="voce@empresa.com" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="company">Empresa</Label>
                <Input id="company" name="company" type="text" placeholder="Nome da empresa" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service">Serviço de interesse</Label>
                <select
                  id="service"
                  name="service"
                  className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Selecionar...</option>
                  <option>Arquitetura de Software</option>
                  <option>Squad as a Service</option>
                  <option>Infraestrutura / DevOps</option>
                  <option>Desenvolvimento Web</option>
                  <option>Desenvolvimento Mobile</option>
                  <option>Cyber Security</option>
                  <option>Data Analytics</option>
                  <option>Outro</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Mensagem *</Label>
              <Textarea
                id="message"
                name="message"
                required
                rows={5}
                placeholder="Conta o problema, contexto, prazo desejado..."
              />
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={status === 'sending'}>
              {status === 'sending' ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Enviando...
                </>
              ) : (
                'Enviar mensagem'
              )}
            </Button>

            {status === 'ok' && (
              <div className="flex items-start gap-3 p-4 rounded-md bg-primary/10 border border-primary/30 text-sm">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-primary">Mensagem enviada!</div>
                  <div className="text-muted-foreground">Voltamos em 1 dia útil.</div>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="flex items-start gap-3 p-4 rounded-md bg-destructive/10 border border-destructive/30 text-sm">
                <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-destructive">Falhou.</div>
                  <div className="text-muted-foreground">{errorMsg}</div>
                </div>
              </div>
            )}
          </motion.form>
        </div>
      </section>
    </>
  )
}
